import React, { useState, useEffect } from 'react';
import { Badge, Breadcrumb, Button, Card, Col, Empty, List, Modal, Popconfirm, Row, Space, Tooltip, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, QuestionCircleOutlined, RightOutlined, HomeOutlined, AppstoreOutlined, DesktopOutlined } from '@ant-design/icons';
import { fetchData, saveData, deleteData } from '../../service/dataServices';
import TranslatableInput from '../../components/form/TranslatableInput';
import { getLocalizedName } from '../../utils/localization';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

const ADDRESS_API = '/api/addresses';

const AddressTab = () => {
    const { t } = useTranslation();
    const [addresses, setAddresses] = useState([]);
    const [selectedHall, setSelectedHall] = useState(null);
    const [selectedZone, setSelectedZone] = useState(null);

    // Modal states
    const [modalVisible, setModalVisible] = useState(false);
    const [modalOperationType, setModalOperationType] = useState('Add');
    const [modalTarget, setModalTarget] = useState(null);
    const [modalEditItem, setModalEditItem] = useState(null);
    const [inputValue, setInputValue] = useState({ en: '', ar: '' });

    const saveMutation = saveData(ADDRESS_API, 'POST');
    const updateMutation = saveData(ADDRESS_API, 'PUT');
    const deleteMutation = deleteData(ADDRESS_API);

    // Fetch all addresses
    fetchData(ADDRESS_API, setAddresses);

    // Derived data
    const halls = addresses || [];
    const zones = selectedHall?.children || [];
    const displayStations = selectedZone?.children || [];

    // Keep selection in sync after refetch — this is what preserves context
    useEffect(() => {
        if (selectedHall && addresses) {
            const updated = addresses.find(h => h.id === selectedHall.id);
            if (updated) {
                setSelectedHall(updated);
                if (selectedZone) {
                    const updatedZone = updated.children?.find(z => z.id === selectedZone.id);
                    if (updatedZone) {
                        setSelectedZone(updatedZone);
                    } else {
                        setSelectedZone(null);
                    }
                }
            } else {
                // Hall was deleted
                setSelectedHall(null);
                setSelectedZone(null);
            }
        }
    }, [addresses]);

    // --- MODAL HELPERS ---
    const getTranslationsFromItem = (item) => {
        if (item && item.translations) {
            return { en: item.translations.en || '', ar: item.translations.ar || '' };
        }
        return { en: item?.name || '', ar: '' };
    };

    const openAddModal = (target) => {
        setModalTarget(target);
        setModalEditItem(null);
        setModalOperationType('Add');
        setInputValue({ en: '', ar: '' });
        setModalVisible(true);
    };

    const openEditModal = (item, target) => {
        setModalTarget(target);
        setModalEditItem(item);
        setModalOperationType('Update');
        setInputValue(getTranslationsFromItem(item));
        setModalVisible(true);
    };

    const onModalOk = () => {
        if (!inputValue.en || !inputValue.en.trim()) return;

        const translations = { en: inputValue.en.trim() };
        if (inputValue.ar && inputValue.ar.trim()) {
            translations.ar = inputValue.ar.trim();
        }

        let dataToSend = {
            name: inputValue.en.trim(),
            type: modalTarget,
            translations: translations,
        };

        if (modalTarget === 'ZONE' && selectedHall) {
            dataToSend.parentId = selectedHall.id;
        } else if (modalTarget === 'DISPLAY_STATION' && selectedZone) {
            dataToSend.parentId = selectedZone.id;
        }

        // Close modal but do NOT clear selections — useEffect will refresh data in place
        if (modalOperationType === 'Add') {
            saveMutation.mutate(
                { dataToSend },
                { onSuccess: () => setModalVisible(false) }
            );
        } else if (modalOperationType === 'Update' && modalEditItem) {
            updateMutation.mutate(
                { id: modalEditItem.id, dataToSend },
                { onSuccess: () => setModalVisible(false) }
            );
        }
    };

    const onDelete = (id, target) => {
        // If deleting the currently selected hall/zone, clear child selection
        if (target === 'HALL' && selectedHall?.id === id) {
            setSelectedHall(null);
            setSelectedZone(null);
        } else if (target === 'ZONE' && selectedZone?.id === id) {
            setSelectedZone(null);
        }
        deleteMutation.mutate({ idList: [id] });
    };

    const getTargetLabel = () => {
        if (modalTarget === 'HALL') return t('objects.hall');
        if (modalTarget === 'ZONE') return t('objects.zone');
        return t('objects.displayStation');
    };

    const renderColumn = (config) => {
        const { title, icon, items, target, selectedItem, onSelect, emptyText, emptyIcon, addDisabled } = config;

        return (
            <Col span={8}>
                <Card
                    size="small"
                    title={
                        <Space>
                            {icon}
                            <span>{title}</span>
                            <Badge count={items.length} style={{ backgroundColor: '#1890ff' }} />
                        </Space>
                    }
                    extra={
                        <Tooltip title={`Add new ${title.toLowerCase().slice(0, -1)}`}>
                            <Button
                                type="primary"
                                ghost
                                icon={<PlusOutlined />}
                                onClick={() => openAddModal(target)}
                                disabled={addDisabled}
                                size="small"
                            >
                                {t('objects.add')}
                            </Button>
                        </Tooltip>
                    }
                    styles={{ body: { padding: '0 8px 8px', maxHeight: 400, overflowY: 'auto' } }}
                >
                    {items.length === 0 ? (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={<Text type="secondary">{emptyText}</Text>}
                            style={{ padding: '24px 0' }}
                        />
                    ) : (
                        <List
                            size="small"
                            dataSource={items}
                            renderItem={item => {
                                const isSelected = selectedItem?.id === item.id;
                                return (
                                    <List.Item
                                        style={{
                                            cursor: 'pointer',
                                            backgroundColor: isSelected ? '#e6f7ff' : 'transparent',
                                            borderLeft: isSelected ? '3px solid #1890ff' : '3px solid transparent',
                                            borderRadius: 4,
                                            padding: '6px 8px',
                                            margin: '2px 0',
                                            transition: 'all 0.2s',
                                        }}
                                        actions={[
                                            <Tooltip key="edit" title="Edit">
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<EditOutlined />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openEditModal(item, target);
                                                    }}
                                                />
                                            </Tooltip>,
                                            <Popconfirm
                                                key="delete"
                                                title={t('objects.confirmation')}
                                                description={t('objects.deleteConfirm')}
                                                icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                                                onConfirm={(e) => {
                                                    e?.stopPropagation();
                                                    onDelete(item.id, target);
                                                }}
                                                onCancel={(e) => e?.stopPropagation()}
                                                okText={t('objects.yes')}
                                            >
                                                <Tooltip title="Delete">
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </Tooltip>
                                            </Popconfirm>,
                                        ]}
                                        onClick={() => onSelect(item)}
                                    >
                                        <Text strong={isSelected}>{getLocalizedName(item)}</Text>
                                    </List.Item>
                                );
                            }}
                        />
                    )}
                </Card>
            </Col>
        );
    };

    return (
        <>
            {/* Breadcrumb showing current context */}
            {(selectedHall || selectedZone) && (
                <div style={{ marginBottom: 16 }}>
                    <Breadcrumb
                        items={[
                            {
                                title: (
                                    <span
                                        style={{ cursor: 'pointer', color: !selectedHall ? '#1890ff' : undefined }}
                                        onClick={() => { setSelectedHall(null); setSelectedZone(null); }}
                                    >
                                        <HomeOutlined /> All Halls
                                    </span>
                                ),
                            },
                            selectedHall ? {
                                title: (
                                    <span
                                        style={{ cursor: 'pointer', color: !selectedZone ? '#1890ff' : undefined }}
                                        onClick={() => setSelectedZone(null)}
                                    >
                                        <AppstoreOutlined /> {getLocalizedName(selectedHall)}
                                    </span>
                                ),
                            } : null,
                            selectedZone ? {
                                title: (
                                    <span style={{ color: '#1890ff' }}>
                                        <DesktopOutlined /> {getLocalizedName(selectedZone)}
                                    </span>
                                ),
                            } : null,
                        ].filter(Boolean)}
                    />
                </div>
            )}

            <Row gutter={16}>
                {renderColumn({
                    title: t('objects.hall') + 's',
                    icon: <HomeOutlined />,
                    items: halls,
                    target: 'HALL',
                    selectedItem: selectedHall,
                    onSelect: (hall) => { setSelectedHall(hall); setSelectedZone(null); },
                    emptyText: 'No halls added yet',
                    addDisabled: false,
                })}
                {renderColumn({
                    title: t('objects.zone') + 's',
                    icon: <AppstoreOutlined />,
                    items: zones,
                    target: 'ZONE',
                    selectedItem: selectedZone,
                    onSelect: (zone) => setSelectedZone(zone),
                    emptyText: selectedHall ? 'No zones in this hall' : 'Select a hall first',
                    addDisabled: !selectedHall,
                })}
                {renderColumn({
                    title: t('objects.displayStation') + 's',
                    icon: <DesktopOutlined />,
                    items: displayStations,
                    target: 'DISPLAY_STATION',
                    selectedItem: null,
                    onSelect: () => { },
                    emptyText: selectedZone ? 'No display stations in this zone' : 'Select a zone first',
                    addDisabled: !selectedZone,
                })}
            </Row>

            <Modal
                title={`${modalOperationType} ${getTargetLabel()}`}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={onModalOk}
                closable={false}
                destroyOnHidden
                centered
                okText={modalOperationType === 'Add' ? t('objects.add') : 'Save'}
            >
                <Space direction="vertical" style={{ display: 'flex', width: '100%' }}>
                    <div style={{ width: '100%' }}>
                        <TranslatableInput
                            value={inputValue}
                            onChange={setInputValue}
                            placeholder={{
                                en: `${getTargetLabel()} name (English)`,
                                ar: `${getTargetLabel()} اسم (عربي)`
                            }}
                        />
                    </div>
                    {modalTarget === 'ZONE' && selectedHall && (
                        <Text type="secondary">
                            Parent: {getLocalizedName(selectedHall)}
                        </Text>
                    )}
                    {modalTarget === 'DISPLAY_STATION' && selectedZone && (
                        <Text type="secondary">
                            Parent: {getLocalizedName(selectedZone)}
                        </Text>
                    )}
                </Space>
            </Modal>
        </>
    );
};

export default AddressTab;